import { useState, useCallback } from "react";
import {
  Box, Typography, Button, Chip, Tooltip, Paper,
  IconButton, TextField, Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// ── Columnas fijas y su label legible ─────────────────────────────────────────
const FIXED_LABELS = {
  id_tipo_contenido: "Tipo Contenido",
  id_caja:           "ID Caja",
  id_tipo_caja:      "Tipo Caja",
  id_sector:         "Sector",
  lote:              "Lote",
};
const FIXED_KEYS = Object.keys(FIXED_LABELS);

// Dado un key dinámico "753_1747502_Concepto", devuelve "Concepto"
const metaLabel = (key) => {
  const parts = key.split("_");
  return parts.length >= 3 ? parts.slice(2).join("_") : key;
};

const colLabel = (key) => FIXED_LABELS[key] ?? metaLabel(key);
const isFixed  = (key) => FIXED_KEYS.includes(key);

// ── Extraer todas las columnas únicas del dataset ─────────────────────────────
const extractColumns = (data) => {
  const seen = new Set();
  const cols = [];
  // Fixed primero, en orden
  FIXED_KEYS.forEach((k) => { if (!seen.has(k)) { seen.add(k); cols.push(k); } });
  data.forEach((row) =>
    Object.keys(row).forEach((k) => { if (!seen.has(k)) { seen.add(k); cols.push(k); } })
  );
  return cols;
};

// ── Componente principal ──────────────────────────────────────────────────────
export function ImportPreview({ data: initialData, onConfirm, onBack }) {
  const [rows, setRows]           = useState(() => initialData.map((r, i) => ({ ...r, _id: i })));
  const [editing, setEditing]     = useState(null); // { rowId, key }
  const [editValue, setEditValue] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const columns = extractColumns(initialData);

  // ── Edición inline ──────────────────────────────────────────────────────────
  const startEdit = useCallback((rowId, key, value) => {
    setEditing({ rowId, key });
    setEditValue(value ?? "");
  }, []);

  const commitEdit = useCallback(() => {
    if (!editing) return;
    setRows((prev) =>
      prev.map((r) => r._id === editing.rowId ? { ...r, [editing.key]: editValue } : r)
    );
    setEditing(null);
  }, [editing, editValue]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") commitEdit();
    if (e.key === "Escape") setEditing(null);
  };

  // ── Eliminar fila ───────────────────────────────────────────────────────────
  const deleteRow = useCallback((rowId) => {
    setRows((prev) => prev.filter((r) => r._id !== rowId));
  }, []);

  // ── Confirmar ───────────────────────────────────────────────────────────────
  const handleConfirm = () => {
    setConfirmed(true);
    // eslint-disable-next-line no-unused-vars
    const clean = rows.map(({ _id, ...rest }) => rest);
    onConfirm(clean);
  };

  if (rows.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <WarningAmberIcon sx={{ fontSize: 48, color: "warning.main", mb: 1 }} />
        <Typography variant="body1" fontWeight={600} gutterBottom>No quedan filas para importar</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Eliminaste todas las filas. Volvé para cargar otro archivo.
        </Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} variant="outlined">
          Volver
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 0.5 }}>
            Vista previa de importación
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Revisá y modificá los datos antes de confirmar. Hacé clic en cualquier celda para editarla.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexShrink: 0 }}>
          <Chip label={`${rows.length} fila${rows.length !== 1 ? "s" : ""}`} size="small" color="primary" variant="outlined" />
          <Chip label={`${columns.length} columnas`} size="small" variant="outlined" />
        </Box>
      </Box>

      <Alert severity="info" sx={{ py: 0.5 }}>
        <Typography variant="caption">
          <strong>Columnas grises</strong> son campos del sistema. <strong>Columnas azules</strong> son metadatos del contenido.
          Podés editar cualquier celda o eliminar filas antes de importar.
        </Typography>
      </Alert>

      {/* Tabla */}
      <Paper
        variant="outlined"
        sx={{ borderRadius: 2, overflow: "hidden" }}
      >
        <Box sx={{ overflowX: "auto", maxHeight: 420, overflowY: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.8rem" }}>
            <thead>
              <tr>
                {/* Columna acciones */}
                <th style={thStyle("#F5F5F5", true)}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary">#</Typography>
                </th>
                {columns.map((col) => (
                  <th key={col} style={thStyle(isFixed(col) ? "#ECEFF1" : "#E3F2FD", true)}>
                    <Tooltip title={col} placement="top" arrow>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        noWrap
                        sx={{ color: isFixed(col) ? "#37474F" : "#1565C0", display: "block", maxWidth: 130 }}
                      >
                        {colLabel(col)}
                      </Typography>
                    </Tooltip>
                  </th>
                ))}
                <th style={thStyle("#F5F5F5", true)}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary">Acción</Typography>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIdx) => (
                <tr key={row._id} style={{ background: rowIdx % 2 === 0 ? "#FFFFFF" : "#FAFAFA" }}>
                  {/* Nro de fila */}
                  <td style={tdStyle("#F5F5F5")}>
                    <Typography variant="caption" color="text.disabled" sx={{ px: 1 }}>
                      {rowIdx + 1}
                    </Typography>
                  </td>

                  {columns.map((col) => {
                    const isEditing = editing?.rowId === row._id && editing?.key === col;
                    const value = row[col] ?? "";
                    return (
                      <td
                        key={col}
                        style={tdStyle(isFixed(col) ? "#FAFAFA" : "#FFF")}
                        onClick={() => !isEditing && startEdit(row._id, col, value)}
                      >
                        {isEditing ? (
                          <TextField
                            autoFocus
                            size="small"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={commitEdit}
                            onKeyDown={handleKeyDown}
                            variant="outlined"
                            sx={{
                              "& .MuiInputBase-input": { fontSize: "0.78rem", py: 0.5, px: 1 },
                              "& .MuiOutlinedInput-root": { borderRadius: 1 },
                              minWidth: 100,
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              display: "flex", alignItems: "center", gap: 0.5,
                              px: 1, py: 0.5, borderRadius: 1, cursor: "pointer",
                              minWidth: 60, maxWidth: 200,
                              "&:hover": { bgcolor: "action.hover" },
                              "&:hover .edit-icon": { opacity: 1 },
                            }}
                          >
                            <Typography
                              variant="caption"
                              noWrap
                              sx={{ flex: 1, color: value === "" ? "text.disabled" : "text.primary" }}
                            >
                              {value === "" ? "—" : String(value)}
                            </Typography>
                            <EditIcon
                              className="edit-icon"
                              sx={{ fontSize: 11, opacity: 0, color: "text.disabled", flexShrink: 0 }}
                            />
                          </Box>
                        )}
                      </td>
                    );
                  })}

                  {/* Eliminar fila */}
                  <td style={tdStyle("#F5F5F5")}>
                    <Tooltip title="Eliminar fila" placement="left">
                      <IconButton
                        size="small"
                        onClick={() => deleteRow(row._id)}
                        sx={{ color: "text.disabled", "&:hover": { color: "error.main" } }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Paper>

      {/* Footer */}
      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", pt: 1 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          variant="outlined"
          disabled={confirmed}
        >
          Volver
        </Button>
        <Button
          startIcon={<CheckCircleOutlineIcon />}
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={confirmed || rows.length === 0}
          sx={{ minWidth: 180 }}
        >
          {confirmed ? "Procesando…" : `Confirmar ${rows.length} fila${rows.length !== 1 ? "s" : ""}`}
        </Button>
      </Box>
    </Box>
  );
}

// ── Helpers de estilo de tabla ─────────────────────────────────────────────────
function thStyle(bg, sticky = false) {
  return {
    background: bg,
    padding: "8px 6px",
    textAlign: "left",
    borderBottom: "2px solid #E0E0E0",
    borderRight: "1px solid #E0E0E0",
    whiteSpace: "nowrap",
    ...(sticky ? { position: "sticky", top: 0, zIndex: 1 } : {}),
  };
}

function tdStyle(bg) {
  return {
    background: bg,
    borderBottom: "1px solid #F0F0F0",
    borderRight: "1px solid #F0F0F0",
    padding: "2px 2px",
    verticalAlign: "middle",
  };
}

export default ImportPreview;
